/**
 * Tenant-by-host edge simulator.
 *
 * In production a reverse proxy (Caddy/Traefik) terminates TLS for the
 * customer's custom domain and forwards the request to this SPA. We then
 * look up the tenant using `GET /api/tenant/by-domain/{host}` and apply the
 * tenant's brand tokens BEFORE the user is logged in — so the marketing +
 * login pages both feel like the customer's own product.
 *
 * When accessed from the default Kairosa preview host we skip the lookup.
 */
import { api } from "@/lib/api";

// Hosts on which Kairosa itself is served (i.e. NOT a customer's custom domain)
const KAIROS_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
]);

const KAIROS_HOST_SUFFIXES = [
  ".emergentagent.com",
  ".kairos.app",
  ".vercel.app",
];

export function isKairosaHost(host = window.location.hostname) {
  if (KAIROS_HOSTS.has(host)) return true;
  return KAIROS_HOST_SUFFIXES.some((s) => host.endsWith(s));
}

export async function loadTenantByHost() {
  const host = window.location.hostname;
  if (isKairosaHost(host)) return null;
  try {
    const { data } = await api.get(`/tenant/by-domain/${encodeURIComponent(host)}`);
    return data;
  } catch {
    return null;
  }
}

// Tailwind's `bg-primary` compiles to `hsl(var(--primary))` — the CSS var MUST
// be a **space-separated HSL triple** ("H S% L%"), NOT a hex or CSS colour
// keyword. When the tenant record stores a hex (from an older branding form)
// we normalise it here; anything already in HSL form passes through untouched.
function normaliseHsl(value) {
  if (!value || typeof value !== "string") return null;
  const raw = value.trim();
  // Already HSL space-separated? e.g. "85 24% 32%"
  if (/^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%$/.test(raw)) return raw;
  // hsl(...) wrapper? strip it.
  const m = raw.match(/^hsla?\(\s*([^)]+)\)$/i);
  if (m) return m[1].replace(/,/g, " ").replace(/\s+/g, " ").trim();
  // Hex? #rgb or #rrggbb → HSL.
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) return hexToHslTriple(raw);
  return null;
}

function hexToHslTriple(hex) {
  let h = hex.slice(1);
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let s = 0;
  let hue = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: hue = ((b - r) / d + 2); break;
      default: hue = ((r - g) / d + 4);
    }
    hue *= 60;
  }
  return `${Math.round(hue)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function applyTenantBranding(tenant) {
  if (!tenant) return;
  const root = document.documentElement;
  const primary = normaliseHsl(tenant.primary_color);
  if (primary) {
    root.style.setProperty("--primary", primary);
    root.style.setProperty("--ring", primary);
    // Ensure the CTA text stays readable regardless of the primary's lightness.
    const l = Number(primary.split(" ")[2].replace("%", ""));
    root.style.setProperty("--primary-foreground", l > 55 ? "20 20% 12%" : "36 40% 96%");
  }
  const accent = normaliseHsl(tenant.accent_color);
  if (accent) {
    root.style.setProperty("--accent", accent);
    const al = Number(accent.split(" ")[2].replace("%", ""));
    root.style.setProperty("--accent-foreground", al > 55 ? "20 20% 12%" : "36 40% 96%");
  }
  if (tenant.name) {
    try {
      document.title = tenant.name;
    } catch {}
  }
}
